import React, { useEffect, useLayoutEffect, useState } from 'react';
import { StandaloneSearchBox, useJsApiLoader } from '@react-google-maps/api';
import { googleMapsProps } from '../../constants';
import { format } from 'src/helpers/pages';
import { defineMessages, FormattedMessage } from 'react-intl';
import Modal from '../../shared/Modal';

const focusAreaKey = 'Program_Information_program_focus_area_'

const focusAreas = [
    {
        color: 'rgb(8,60,202)',
        icon: "fas fa-book-open",
        label: 'Academic Enrichment & Literacy',
        value: 'Academic Enrichment & Literacy',
    },
    {
        color: 'rgb(227,37,103)',
        icon: "fas fa-palette",
        label: 'Arts',
    },
    {
        color: 'rgb(24,178,237)',
        icon: "fas fa-graduation-cap",
        label: 'College & Career Readiness',
    },
    {
        color: 'rgb(234,187,61)',
        icon: "fas fa-users",
        label: 'Clubs',
    },
    {
        color: 'rgb(66,56,91)',
        icon: "fas fa-calculator",
        label: 'Math',
    },
    {
        color: 'rgb(234,187,61)',
        icon: "fas fa-hand-holding-heart",
        label: 'Social Emotional Learning (SEL)',
    },
    {
        color: 'rgb(18,46,86)',
        icon: "fas fa-basketball-ball",
        label: 'Sports & Health & Wellness',
    },
    {
        color: 'rgb(155,49,58)',
        icon: "fas fa-flask",
        label: 'STEM/STEAM',
    },
]

export default function CustomOneFilterBar({ database, intl, term, setTerm, setLocation, location, filter, setDistance, distance, setFilter, onSearch, onClearFilters, onKeyDown }) {
    const [searchBox, setSearchBox] = useState<google.maps.places.SearchBox>()
    const [searchBoxModal, setSearchBoxModal] = useState<google.maps.places.SearchBox>()
    const [hasEdits, setHasEdits] = useState<boolean>(false)

    useLayoutEffect(() => {
        document?.getElementById?.("searchAddressBar")?.parentElement?.classList?.add?.('col')
        document?.getElementById?.("searchAddressBar")?.parentElement?.classList?.add?.('mx-0')
        document?.getElementById?.("searchAddressBar")?.parentElement?.classList?.add?.('pr-0')
    })

    const { isLoaded } = useJsApiLoader(googleMapsProps as any)

    const messages = defineMessages({
        searchPrompt: {
            id: 'search.form.placeholder',
            defaultMessage: 'Type in keywords or use filters to narrow down search...',
        },
        bryanKeywordSearchPrompt: {
            id: 'search.form.placeholder',
            defaultMessage: 'Search by Keyword',
        },
        bryanAddressSearchPrompt: {
            id: 'search.form.placeholder',
            defaultMessage: 'Search by Address',
        },
    });

    const handleAutoCompletePlaceChange = async (sB) => {
        if (!sB) {
            setLocation({})
            setHasEdits(true)
        } else if (sB?.getPlaces?.()?.length) {
            const place = sB?.getPlaces?.()?.[0]
            setLocation({ lat: place?.geometry?.location?.lat(), lng: place?.geometry?.location?.lng(), address: place?.formatted_address })
            setHasEdits(true)
        }
    };

    const toggleFilterValue = (key: string, value: string) => {
        setHasEdits(true)
        let filterValue = filter[key]
        if (!Array.isArray(filterValue)) filterValue = [filterValue].filter(Boolean)
        if (filterValue?.includes?.(value))
            setFilter(key, filterValue.filter(v => v !== value))
        else setFilter(key, [...filterValue, value])
    }

    const success = (pos) => {
        const crd = pos?.coords;
        if (crd?.latitude && crd?.longitude) {
            setLocation({ lat: crd.latitude, lng: crd.longitude, address: 'Current Location' })
            setHasEdits(true)
        }
    }

    const requestCurrentLocation = () => {
        const options = {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
        };
        navigator?.geolocation?.getCurrentPosition?.(success, () => alert('Failed to load your location. Please make sure you have location services turned on.'), options);
    }

    const renderKeywordSearch = () => {
        return (
            <div style={{ flex: 1, width: '100%' }}>
                {/* <label>Search</label> */}
                <input
                    id="searchBar"
                    type="text"
                    style={{ width: '100%' }}
                    className="form-control"
                    placeholder={intl.formatMessage(
                        messages.bryanKeywordSearchPrompt
                    )}
                    onChange={(e) => { setTerm(e.currentTarget.value); setHasEdits(true) }}
                    value={term}
                    onKeyDown={onKeyDown}
                />
            </div>
        )
    }

    const renderFilters = () => {
        const filterCount = Object.keys(filter)?.filter?.(Boolean)?.length
        return (<>
            <div className='d-none d-md-block pt-2'>
                <div className="d-flex">
                    {renderKeywordSearch()}
                    <button type="button" className={`btn btn-${hasEdits ? 'primary' : 'secondary'} ${hasEdits && 'blob'} mx-2`} onClick={onSearch}>
                        <i className="fas fa-search" />
                    </button>
                </div>
            </div>
            <div className='d-md-none pt-2'>
                <div className="d-flex mr-3">
                    {renderKeywordSearch()}
                    <button
                        className={`btn btn-link mr-2`}
                        style={(filterCount || location) ? {} : { color: 'grey' }}
                        data-toggle="collapse"
                        data-target="#collapseFilters">
                        <i className="fas fa-filter" />
                        <div style={{ fontSize: 8, textAlign: 'center' }}>Filters</div>
                    </button>
                    <button type="button" className={`btn btn-${hasEdits ? 'primary' : 'secondary'} ${hasEdits && 'blob'}`} onClick={onSearch}>
                        <i className="fas fa-search" />
                    </button>
                </div>
            </div>
        </>)
    }

    const actionButtons = [
        <button type="button" className="btn btn-link" key="reset-button" onClick={() => {
            onClearFilters(document.getElementById('searchAddressBar')?.style?.display !== 'block')
            $('#FiltersModal').modal('hide')
        }}>
            <FormattedMessage id="dash.filter.btn.save" defaultMessage="Reset Filters" />
        </button>,
        <button type="button" className="btn btn-primary" key="search-button" onClick={onSearch}>
            <FormattedMessage id="dash.filter.btn.save" defaultMessage="Search" />
        </button>
    ];

    const renderSearchFilterOptions = (key: string, option: string) => (
        <div className="form-check" key={`option-${key}-${option}`}>
            <input
                className="form-check-input"
                type="checkbox"
                value={option}
                id={option}
                onChange={() => toggleFilterValue(key, option)}
                checked={Array.isArray(filter[key]) ? !!filter[key]?.includes?.(option) : !![filter[key]]?.includes?.(option)}
            />
            <label className="form-check-label" htmlFor={option}>{option}</label>
        </div>
    )

    const renderSearchFilters = () => {
        const filters = database && database.filters ? database.filters : [];
        return (
            <>
                {!database ||
                    filters.map((x) => {
                        const field = database.fields.find(
                            (f) => format(f.group, f.name) === x
                        );
                        if (!field || x === focusAreaKey) return null;
                        return (
                            <div
                                key={field.name}
                                className="col-md-4 col-sm-6 form-group"
                            >
                                <label className="font-weight-bold" htmlFor={field.name}>{field.label}</label>
                                <div className='d-flex d-flex-row'>
                                    <div className="mr-1">{field.options.filter(o => !o.includes('This provider')).slice(0, 7).map(o => renderSearchFilterOptions(x, o))}</div>
                                    <div>{field.options.filter(o => !o.includes('This provider')).slice(7).map(o => renderSearchFilterOptions(x, o))}</div>
                                </div>
                            </div>
                        );
                    })}
            </>
        );
    }

    const renderSearchFocusAreas = () => (
        <>
            <div className="row my-3">
                <h5 className="col">Program Focus Area</h5>
            </div>
            <div className="row">
                {focusAreas.map(f => {
                    const selected = filter[focusAreaKey]?.includes(f.label)
                    return (
                        <div key={f.label} className='col-lg-6 col-xs-12'>
                            <button className="primary-focus-btn mb-2 w-100" style={{ borderRadius: 5, background: selected ? f.color : 'white' }} onClick={() => toggleFilterValue(focusAreaKey, f.label)}>
                                <div className="d-flex align-items-center p-2 font-weight-bold" style={{ color: selected ? 'white' : 'black' }}>
                                    <div className="mr-2">
                                        <em className={f.icon} style={{ background: f.color }} />
                                    </div>
                                    <div className="text-center" style={{ width: '100%' }}>
                                        {f.label}
                                    </div>
                                </div>
                            </button>
                        </div>
                    )
                })}
            </div>
        </>
    )

    const renderFiltersModal = () => {
        return (
            <Modal
                large
                id="FiltersModal"
                title="Filters"
                style={{ backgroundColor: '#f5f5f5' }}
                actionButtons={actionButtons}>
                <div className="d-md-none d-block">
                    <div className="mb-2">
                        {/* renderAddressSearch("searchAddressBarModal", searchBoxModal, setSearchBoxModal, true) */}
                    </div>
                </div>
                <div className="mb-2">
                    {renderSearchFocusAreas()}
                </div>
                <div className='row'>
                    {renderSearchFilters()}
                </div>
            </Modal >
        )
    }

    return (
        <div style={{ height: 65, width: '100%', boxShadow: '0 0 3px 0 rgba(0,0,0,.1)' }}>
            {renderFilters()}
        </div>
    )
}


// WEBPACK FOOTER //
// ./src/public/search/CustomOneFilterBar.tsx